import React, { useCallback, useEffect, useState } from 'react';

import {
  WithPopover,
  Flexbox,
  FontAwesomeIcon,
  Text,
} from '@schoolinks/ui-components';
import cn from 'classnames';
import { last, noop } from 'lodash';
import { bool, func, string } from 'prop-types';

import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';

import { useDispatch } from 'react-redux';

import { faAngleDown } from '@fortawesome/pro-regular-svg-icons/faAngleDown';
import { faLanguage } from '@fortawesome/pro-regular-svg-icons/faLanguage';

import { toggleGoogleTranslate } from 'actions/googleTranslate';
import TranslationDrawer from 'components/Common/GoogleTranslateV2/components/TranslationDrawer';
import {
  DEFAULT_LANGUAGE_ENGLISH,
  EL_SCRIPT_SRC,
  GOOGLE_TRANSLATE_COOKIE_KEY,
  INITIAL_LANGUAGE,
  LANGUAGE_OPTIONS,
  SCRIPT_SELECTORS,
  SCRIPT_SRC,
  EL_LANGUAGE_OPTIONS,
  I18N_TRANSLATE_COOKIE_KEY,
} from 'components/Common/GoogleTranslateV2/constants';
import {
  getDropdownVisibility,
  handleSetLanguage,
} from 'components/Common/GoogleTranslateV2/utils';
import { debounceIntervalLong } from 'constants/debounceInterval';
import Api from 'core/api';
import useMediaInfo from 'hooks/useMediaInfo';
import useMutation from 'hooks/useMutation';
import useUserRedux from 'hooks/useUserRedux';
import dayjs from 'services/dayjs';
import { isNonEmptyArray } from 'utils/arrays';
import { loadScript } from 'utils/dom';

import s from './GoogleTranslate.css';

import './GoogleTranslateOverrides.css';

function initGoogleTranslate(isElementary) {
  const layoutCode = 0;
  const paramsBase = {
    pageLanguage: DEFAULT_LANGUAGE_ENGLISH,
    layout: layoutCode,
    autoDisplay: false,
    gaTrack: true,
    gaId: 'UA-43742130-1',
  };

  const allLanguages =
    'sq,am,ar,hy,bn,bs,bg,zh-CN,zh-TW,nl,en,tl,fr,de,el,gu,ht,iw,hi,hmn,ig,ilo,it,ja,km,ko,lo,ml,ne,ps,fa,pl,pt,pt-PT,pa,ro,ru,sr,sd,so,es,sw,ta,te,th,tr,uk,ur,vi,yo,rw,my,mn';
  const allParams = {
    ...paramsBase,
    includedLanguages: isElementary ? 'en,es' : allLanguages,
  };
  // eslint-disable-next-line no-new
  new window.google.translate.TranslateElement(
    allParams,
    'google_translate_element',
  );
}

function initGoogleTranslateElementary() {
  initGoogleTranslate(true);
}

window.initGoogleTranslateElementary = initGoogleTranslateElementary;
window.initGoogleTranslate = initGoogleTranslate;

/**
 * @param {string} className Class of the translate button
 * @param {boolean<false>} hidden If true, we hide the translate button from it's placement
 * @param {boolean<false>} isElementary If true, we load scripts correctly for elementary users
 * @param {func} onClose Close translation drawer on tablet/mobile
 * @param {boolean<false>} isOnboarding If true, dropdown is in onboarding flow
 * @param {boolean<false>} isAppbar If true, dropdown gets appbar styles
 * @returns @GoogleTranslate
 */
const GoogleTranslate = ({
  className,
  hidden,
  isElementary,
  isOnboarding,
  onClose,
  isAppbar,
}) => {
  const { isDesktop } = useMediaInfo();
  const { isLoggedIn } = useUserRedux();
  const [cookies, setCookie, removeCookie] = useCookies([
    GOOGLE_TRANSLATE_COOKIE_KEY,
    I18N_TRANSLATE_COOKIE_KEY,
  ]);
  const { i18n } = useTranslation();
  const selectedGoogleLanguage = cookies[GOOGLE_TRANSLATE_COOKIE_KEY];
  const isI18NLanguageSelected =
    cookies[I18N_TRANSLATE_COOKIE_KEY] &&
    cookies[I18N_TRANSLATE_COOKIE_KEY] !== DEFAULT_LANGUAGE_ENGLISH;
  const dispatch = useDispatch();

  const { mutateAsync: setUserLanguage } = useMutation({
    apiFunc: Api.slUsers.updateUserDetails,
  });

  const getInitialLanguage = useCallback(() => {
    if (selectedGoogleLanguage) {
      const googleLanguageKey = last(selectedGoogleLanguage.split('/'));
      return LANGUAGE_OPTIONS.find(
        option => option.value === googleLanguageKey,
      );
    }
    const currentI18nLanguage = LANGUAGE_OPTIONS.find(
      option => option.value === i18n.language,
    );

    return currentI18nLanguage || INITIAL_LANGUAGE;
  }, [selectedGoogleLanguage, i18n.language]);

  const [selectedLanguage, setSelectedLanguage] =
    useState(getInitialLanguage());
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false);

  const scriptSrc = isElementary ? EL_SCRIPT_SRC : SCRIPT_SRC;

  const reApplyScript = useCallback(() => {
    // Select the element that's embedded into DOM via script
    const scriptEl = document.querySelector(`script[src="${scriptSrc}"]`);

    // If element already exist, we remove the previously embedded element, to be able to create a new one
    if (scriptEl) {
      scriptEl.parentNode.removeChild(scriptEl);
    }

    // Iterate through script element selectors and remove previously embedded ones into DOM
    SCRIPT_SELECTORS.forEach(selector => {
      const elementToRemove = Array.prototype.slice.call(
        document.querySelectorAll(selector),
      );

      if (isNonEmptyArray(elementToRemove)) {
        elementToRemove.forEach(element => {
          element.parentNode.removeChild(element);
        });
      }
    });

    // Embed new element google translate element into DOM via the script
    loadScript(scriptSrc);
  }, [scriptSrc]);

  useEffect(() => {
    // Set a debounced loader by 1s, to be able to re-apply script to refresh translated items if they haven't.
    const debouncedLoader = setTimeout(() => {
      reApplyScript();
    }, debounceIntervalLong);

    // Clear the timeout when we close the google translator
    return () => clearTimeout(debouncedLoader);
  }, [hidden, reApplyScript, selectedGoogleLanguage]);

  // Sync the redux state once so that the dropdown shows in the appbar on page load if translations are applied
  useEffect(() => {
    if (!isDesktop || hasLoadedInitial || isOnboarding) {
      return;
    }
    const hasTranslations =
      !!isI18NLanguageSelected || !!selectedGoogleLanguage;
    dispatch(toggleGoogleTranslate(hasTranslations));
    setHasLoadedInitial(true);
  }, [
    isDesktop,
    isI18NLanguageSelected,
    selectedGoogleLanguage,
    dispatch,
    hasLoadedInitial,
    isOnboarding,
  ]);

  // Set locale to handle translations for formatted date strings
  useEffect(() => {
    dayjs.locale(selectedLanguage?.value ?? 'en');
  }, [selectedLanguage?.value]);

  // Close the drawer when resizing the window
  useEffect(() => {
    if (isDesktop) {
      return;
    }

    dispatch(toggleGoogleTranslate(false));
  }, [isDesktop, dispatch]);

  // Tablet/mobile version will be handled separately
  const showDropdown = getDropdownVisibility({
    isDesktop,
    isOnboarding,
    hidden,
    selectedGoogleLanguage,
    isI18NLanguageSelected,
  });

  const onChange = val =>
    handleSetLanguage({
      val,
      i18n,
      setSelectedLanguage,
      removeCookie,
      setCookie,
      selectedLanguage,
      // On desktop, don't pass onClose so that dropdown persists in appbar
      onClose: isDesktop ? noop : onClose,
      setUserLanguage: isLoggedIn ? setUserLanguage : noop,
    });

  const languageDrawerOpen = !hidden && !isDesktop;

  const languageOptions = isElementary ? EL_LANGUAGE_OPTIONS : LANGUAGE_OPTIONS;

  return (
    <>
      <div id="google_translate_element" className={cn(className, s.root)} />
      {showDropdown && (
        <WithPopover
          anchorButtonClass={cn(s.anchorButton, { [s.appbarButton]: isAppbar })}
          tooltipProps={{
            placement: 'bottom',
            title: 'Select your preferred language',
          }}
          paperClass={s.menuContainer}
          anchorContent={({ open }) => (
            <Flexbox
              data-testid="translation-dropdown"
              alignItems="center"
              className={cn(s.anchorContainer, {
                [s.onboardingAnchorContainer]: isOnboarding,
              })}
            >
              <FontAwesomeIcon color="default" icon={faLanguage} />
              <Text ml={0.5} mr={1} variant="bodySm600">
                {selectedLanguage.label}
              </Text>
              <FontAwesomeIcon
                color="default"
                icon={faAngleDown}
                rotation={open ? 180 : 0}
              />
            </Flexbox>
          )}
          menuProps={{
            selectedId: selectedLanguage.value,
            items: languageOptions.map(option => ({
              textContent: option.label,
              onClick: () => onChange(option),
              id: option.value,
            })),
          }}
        />
      )}
      <TranslationDrawer
        isElementary={isElementary}
        languageDrawerOpen={languageDrawerOpen}
        languageOptions={languageOptions}
        onChange={onChange}
        onClose={onClose}
        selectedLanguage={selectedLanguage}
      />
    </>
  );
};

GoogleTranslate.propTypes = {
  className: string,
  hidden: bool,
  isElementary: bool,
  isOnboarding: bool,
  onClose: func,
  isAppbar: bool,
};

GoogleTranslate.defaultProps = {
  className: undefined,
  hidden: false,
  isElementary: false,
  isOnboarding: false,
  onClose: noop,
  isAppbar: false,
};

export default GoogleTranslate;
